import { PaChatView, resolveThreadKind } from '@formant/chat';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useChatPage } from '../../components/chat/ChatPage';
import { ConnectedThreadsStrip } from '../../components/chat/ConnectedThreads';
import { LegacyThreadView } from '../../components/chat/LegacyThreadView';
import { formantChatAgentId } from '../../components/chat/ThreadList';
import { initialMessageFor } from '../../components/chat/chat-page-helpers';
import { renderMemoryToolCall } from '../../components/chat/host-seams';

/**
 * Conversation view for a deep-linked chat thread (`/chat/$threadId`,
 * DEV-16971). Resolves whether the id is a PA session or a legacy theopolis
 * thread from the already-loaded sidebar lists, then renders the matching
 * conversation. A first message queued by the greeting composer (if this is the
 * thread it just created) is handed to PaChatView to send once.
 */
function ChatThreadRoute() {
  const { threadId } = Route.useParams();
  const { session, chatAgent, pendingFirstMessage, clearPendingFirstMessage } = useChatPage();

  const paItems = session.paSessions.data?.items ?? [];
  const legacyItems = session.legacyThreads.data ?? [];
  const loading = session.paSessions.isLoading || session.legacyThreads.isLoading;
  // Legacy chat threads are host-supplied `extra` rows to the package; their ids
  // feed the generic `resolveThreadKind` (`'extra'` == legacy on this surface).
  const extraIds = legacyItems
    .filter((t) => t.agentId === formantChatAgentId && !t.archived)
    .map((t) => t.id);
  const kind = resolveThreadKind({ id: threadId, paItems, extraIds, loading });

  const initialMessage = initialMessageFor(pendingFirstMessage, threadId);

  // Consume the queued first message once it belongs to this thread, so a later
  // navigation back to a bare thread never replays it.
  useEffect(() => {
    if (pendingFirstMessage?.threadId === threadId) clearPendingFirstMessage();
  }, [threadId, pendingFirstMessage, clearPendingFirstMessage]);

  if (kind === 'loading') {
    return <div className="chat-launcher__loading">Loading…</div>;
  }

  if (kind === 'extra') {
    const legacyThread = legacyItems.find((t) => t.id === threadId);
    if (!legacyThread) return <div className="chat-launcher__loading">Loading…</div>;
    return (
      <LegacyThreadView
        key={threadId}
        thread={legacyThread}
        agent={chatAgent}
        thinking={session.thinking}
        onThinkingChange={session.setThinking}
      />
    );
  }

  const currentName = paItems.find((t) => t.id === threadId)?.name ?? null;

  return (
    <PaChatView
      key={threadId}
      groupId={session.groupId}
      threadId={threadId}
      thinking={session.thinking}
      onThinkingChange={session.setThinking}
      onNavigate={session.onNavigate}
      onTurnComplete={(firstMessage) =>
        session.autoNamePaThread(threadId, currentName, firstMessage)
      }
      autoReadAloud={session.autoReadAloud}
      onAutoReadAloudChange={session.setAutoReadAloud}
      initialMessage={initialMessage}
      renderToolCall={renderMemoryToolCall}
      renderAboveComposer={({ threadId: tid, transcript }) => (
        <ConnectedThreadsStrip threadId={tid} transcript={transcript} />
      )}
    />
  );
}

export const Route = createFileRoute('/chat/$threadId')({ component: ChatThreadRoute });
